/**
 * A creator studio's autosave, as a hook.
 *
 * Both studios have the same problem and the same answer: a creator builds work
 * over an evening on a phone, and until they save it a reload, a crash, a low
 * battery or a back gesture takes all of it. So while they write, the studio keeps
 * a copy of the work on the device and offers it back the next time it opens.
 *
 * The rules are subtle enough that there should be exactly one of them, which is
 * why this is shared rather than written twice — and why it is kept out of the
 * screens: threading a timer and a lifecycle through a screen that is already
 * thousands of lines long is how the one place a creator's work can go missing
 * becomes invisible.
 *
 * What it does, and what it deliberately does not:
 *
 *  - It writes a **recovery snapshot**, debounced, so a burst of typing costs one
 *    write rather than one per keystroke.
 *  - It **flushes** rather than merely cancelling: leaving the screen, or the app
 *    going to the background — which on the web is what a reload looks like —
 *    writes whatever was still waiting. A crash cannot be caught, so the promise
 *    is "at most the last second of typing", not "nothing is ever lost".
 *  - It **clears** when there is nothing left to keep, but *only a copy it wrote
 *    itself* (see `written`).
 *  - It does **not** touch the server. Saving is the creator's decision — the
 *    drafts feature is built on that — and a device-local copy is the app
 *    remembering for them.
 *
 * Two rules make it safe to run at all, and both exist because the obvious version
 * destroyed the thing it was built to protect:
 *
 *  - **A session only keeps work it produced.** `keep` is not "the work differs
 *    from the last save" — that is true the instant a draft is opened, before the
 *    creator has touched anything. It is "something has changed *since this screen
 *    opened*", so a session that is only reading what was already there never
 *    writes. Without this, opening the studio overwrote the previous session's copy
 *    with the pristine work within a second of loading, and offered *that* back.
 *  - **A session only throws away a copy it wrote.** The storage slot holds the
 *    last session's unfinished work when the studio opens, and it is exactly what
 *    is on offer — so clearing it on the way in (which the "nothing to keep" path
 *    does) would delete the offer. The copy belongs to whoever wrote it, and to the
 *    creator, who discards it explicitly.
 *
 * Between them the slot means one thing: the work as this device last saw it being
 * written.
 */
import {useCallback, useEffect, useRef, useState} from 'react'
import {AppState} from 'react-native'

export type AutosaveState =
  /** Nothing to keep: no work of this session's, or nothing to say about it. */
  | 'idle'
  /** A write is scheduled, or has just been made. */
  | 'saving'
  /** The work is on the device. */
  | 'saved'
  /** The device would not take it — almost always a full store. */
  | 'failed'

export type StudioAutosave = {
  state: AutosaveState
  /** When the last copy was written, for the status line. */
  savedAt: string | null
  /** Writes a pending change now instead of waiting out the debounce. */
  flush: () => void
  /** Throws the copy away: published, discarded, or replaced by other work. */
  discard: () => void
}

/** Long enough that a burst of typing is one write, short enough to feel live. */
const DEFAULT_DELAY_MS = 900

export function useStudioAutosave<TSnapshot>({
  signature,
  keep,
  take,
  write,
  clear,
  delayMs = DEFAULT_DELAY_MS,
}: {
  /** The work's content fingerprint — the thing that changes when it changes. */
  signature: string
  /**
   * Whether this session has work worth keeping.
   *
   * Both halves matter: the work is unsaved, *and* it differs from the work this
   * screen opened with. The caller owns that second half because only the caller
   * knows what the session started from — and it is the half that keeps a session
   * from writing over the copy it has not offered back yet.
   */
  keep: boolean
  /**
   * Builds the snapshot to store, given the moment it is being written.
   *
   * Called at write time rather than taken as a value, so the effect can depend on
   * the *fingerprint* alone: work rebuilt on every render would otherwise restart
   * the debounce on every render, and the timer would never fire.
   */
  take: (savedAt: string) => TSnapshot
  /** Puts the snapshot in its slot. */
  write: (snapshot: TSnapshot) => void
  /** Empties the slot. */
  clear: () => void
  delayMs?: number
}): StudioAutosave {
  const [state, setState] = useState<AutosaveState>('idle')
  const [savedAt, setSavedAt] = useState<string | null>(null)

  /** The latest of everything, read when the timer fires rather than captured. */
  const latest = useRef({signature, keep, take, write, clear})
  latest.current = {signature, keep, take, write, clear}

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  /**
   * Whether a change is waiting to be written.
   *
   * Separate from the timer, and deliberately not cleared when the timer is:
   * rescheduling a debounce and cancelling one look identical from the outside,
   * and the flush below has to tell them apart or it will either lose the last
   * second of typing or write back a copy the creator just threw away.
   */
  const pending = useRef(false)
  /**
   * Whether *this* session is the one that put the copy in the slot.
   *
   * The slot belongs to whoever wrote it. At open it holds the previous session's
   * unfinished work — the copy this session may be about to offer back — and a
   * session that has not written anything has no business deleting it.
   */
  const written = useRef(false)

  const cancel = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
  }, [])

  const settle = useCallback(() => {
    cancel()
    pending.current = false
    const now = latest.current
    if (!now.keep) return
    try {
      const takenAt = new Date().toISOString()
      now.write(now.take(takenAt))
      written.current = true
      setSavedAt(takenAt)
      setState('saved')
    } catch {
      /*
       * A full device store is the realistic failure, and it is not exotic: work
       * that references a `data:` URL can outgrow the few megabytes a browser
       * gives localStorage on its own. Reporting it is the whole point — a copy
       * that silently stopped being taken is worse than no copy, because the
       * creator has stopped worrying.
       */
      setState('failed')
    }
  }, [cancel])

  const flush = useCallback(() => {
    if (!pending.current) return
    settle()
  }, [settle])

  const discard = useCallback(() => {
    cancel()
    pending.current = false
    written.current = false
    latest.current.clear()
    setSavedAt(null)
    setState('idle')
  }, [cancel])

  useEffect(() => {
    if (!keep) {
      // Nothing to keep. This is the path a successful save takes, which is what
      // makes the snapshot a record of *unsaved* work and nothing else.
      cancel()
      pending.current = false
      /*
       * And only our own copy goes. A copy this session never wrote is the
       * previous session's work, still on offer — deleting it here is how a
       * reload used to destroy the very thing it was meant to hand back.
       */
      if (written.current) {
        latest.current.clear()
        written.current = false
        setSavedAt(null)
      }
      setState('idle')
      return
    }
    // A change restarts the debounce, so a burst of typing is one write.
    setState('saving')
    cancel()
    pending.current = true
    timer.current = setTimeout(settle, delayMs)
    // `cancel` and not `settle`: the pending flag has to survive the reschedule
    // that happens on the next keystroke.
    return cancel
  }, [cancel, delayMs, keep, settle, signature])

  /*
   * Leave nothing in flight behind.
   *
   * Unmounting the studio is the ordinary way a session ends — back to Home, or a
   * publish that navigates away — and the pending write is the last second of the
   * creator's typing. A synchronous device write is cheap enough to do in a
   * cleanup, and `flush` is guarded by the same `keep` check the scheduled write
   * uses, so a session that has produced nothing cannot write over the copy it is
   * still offering back.
   */
  useEffect(() => {
    return () => {
      flush()
    }
  }, [flush])

  /*
   * And for the ways a session ends that are not React's.
   *
   * On the web a reload is a visibility change, and on a phone an app is
   * backgrounded long before it is closed — neither unmounts the studio, and both
   * are exactly when someone stops writing. `AppState` covers the two platforms
   * with one listener; React Native Web maps it onto `visibilitychange`.
   */
  useEffect(() => {
    const sub = AppState.addEventListener('change', next => {
      if (next === 'background' || next === 'inactive') flush()
    })
    return () => sub.remove()
  }, [flush])

  return {state, savedAt, flush, discard}
}
